/**
 * Memory-Based Token Storage
 * 
 * Implements secure in-memory storage for access tokens.
 * Access tokens are kept in memory only and never persisted to storage.
 * 
 * Security Rationale:
 * - Access tokens are short-lived and should not persist across page refreshes
 * - XSS attacks cannot extract tokens from memory as easily as from storage
 * - Reduces attack surface by minimizing token exposure in storage
 * 
 * Note: Refresh tokens may still be stored (encrypted) for session persistence,
 * but access tokens remain in memory only.
 */

interface MemoryTokenStorage {
  accessToken: string | null;
  refreshToken: string | null;
}

/**
 * In-memory token storage
 * Tokens are stored in a closure to prevent direct access
 */
class MemoryTokenStorageService {
  private tokens: MemoryTokenStorage = {
    accessToken: null,
    refreshToken: null,
  };

  /**
   * Get access token from memory
   * Returns null if token is not in memory (e.g., after page refresh)
   */
  getAccessToken(): string | null {
    return this.tokens.accessToken;
  }

  /**
   * Set access token in memory
   * This will be lost on page refresh - this is intentional for security
   */
  setAccessToken(token: string): void {
    this.tokens.accessToken = token;
  }

  /**
   * Clear access token from memory
   */
  clearAccessToken(): void {
    this.tokens.accessToken = null;
  }

  /**
   * Get refresh token from memory
   * Note: Refresh tokens may also be stored in encrypted storage for persistence
   */
  getRefreshToken(): string | null {
    return this.tokens.refreshToken;
  }

  /**
   * Set refresh token in memory
   */
  setRefreshToken(token: string): void {
    this.tokens.refreshToken = token;
  }

  /**
   * Clear refresh token from memory
   */
  clearRefreshToken(): void {
    this.tokens.refreshToken = null;
  }

  /**
   * Clear all tokens from memory
   */
  clearAll(): void {
    this.tokens.accessToken = null;
    this.tokens.refreshToken = null;
  }

  /**
   * Check if access token exists in memory
   */
  hasAccessToken(): boolean {
    return this.tokens.accessToken !== null;
  }

  /**
   * Check if refresh token exists in memory
   */
  hasRefreshToken(): boolean {
    return this.tokens.refreshToken !== null;
  }
}

// Singleton instance
const memoryTokenStorage = new MemoryTokenStorageService();

export { memoryTokenStorage };
export type { MemoryTokenStorage };

